import React, { useState } from 'react';
import { generateQuestions, exploreTopic } from '../../services/api';
import { Question, TopicExplorationResponse } from '../../types';

interface LearningModeProps {
  conversationSummary: string;
  conversationHistory?: string;
  sourceLanguage: string;
  targetLanguage: string;
  context?: string;
  translationProvider?: string;
}

const LearningMode: React.FC<LearningModeProps> = ({
  conversationSummary,
  conversationHistory,
  sourceLanguage,
  targetLanguage,
  context,
  translationProvider = 'dashscope',
}) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [exploringTopic, setExploringTopic] = useState<string | null>(null);
  const [topicExploration, setTopicExploration] = useState<TopicExplorationResponse | null>(null);
  const [isExploring, setIsExploring] = useState<boolean>(false);

  const handleGenerateQuestions = async () => {
    if (!conversationSummary || !conversationSummary.trim()) {
      setError('No summary available. Please generate a summary first.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setQuestions([]);
    setTopicExploration(null);

    try {
      const result = await generateQuestions(
        conversationSummary,
        conversationHistory || null,
        sourceLanguage,
        targetLanguage,
        targetLanguage, // question language defaults to target language
        5, // num questions
        null, // model - use default
        translationProvider,
        context || null
      );
      setQuestions(result.questions);
    } catch (err) {
      console.error('Question generation error:', err);
      setError('Failed to generate questions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExploreTopic = async (question: Question) => {
    setIsExploring(true);
    setExploringTopic(question.topic);
    setError(null);

    try {
      const result = await exploreTopic(
        question.topic,
        question.question,
        conversationHistory || null,
        targetLanguage,
        null, // model - use default
        translationProvider
      );
      setTopicExploration(result);
    } catch (err) {
      console.error('Topic exploration error:', err);
      setError('Failed to explore topic. Please try again.');
      setTopicExploration(null);
    } finally {
      setIsExploring(false);
    }
  };

  const getDifficultyColor = (difficulty?: string) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'hard':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    }
  };

  const hasContent = questions.length > 0;

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <svg
            className="w-5 h-5 text-primary-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
          Learning Mode
        </h3>
      </div>

      {error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {hasContent && (
        <div className="mb-3">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Generated Questions ({questions.length})
          </h4>
          <div className="space-y-2">
            {questions.map((question, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-3 hover:border-primary-300 hover:shadow-sm transition-all cursor-pointer"
                onClick={() => handleExploreTopic(question)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-sm text-gray-800 font-medium mb-1">
                      {question.question}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-gray-500">
                        Topic: {question.topic}
                      </span>
                      {question.difficulty && (
                        <span
                          className={`text-xs px-2 py-0.5 rounded border ${getDifficultyColor(
                            question.difficulty
                          )}`}
                        >
                          {question.difficulty}
                        </span>
                      )}
                    </div>
                  </div>
                  <svg
                    className="w-4 h-4 text-primary-500 flex-shrink-0 mt-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {topicExploration && (
        <div className="mb-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-blue-900">
              {topicExploration.topic}
            </h4>
            <button
              onClick={() => {
                setTopicExploration(null);
                setExploringTopic(null);
              }}
              className="text-blue-600 hover:text-blue-800"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">
            {topicExploration.explanation}
          </p>
          {topicExploration.key_points && topicExploration.key_points.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-gray-700 mb-1">Key Points:</h5>
              <ul className="list-disc list-inside space-y-1">
                {topicExploration.key_points.map((point, idx) => (
                  <li key={idx} className="text-xs text-gray-600">
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <button
        onClick={handleGenerateQuestions}
        disabled={isLoading || !conversationSummary || !conversationSummary.trim()}
        className={`w-full px-4 py-2 rounded-lg font-semibold text-white transition-all ${
          isLoading
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-primary-500 hover:bg-primary-600'
        } disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
      >
        {isLoading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>Generating Questions...</span>
          </>
        ) : (
          <>
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>Generate Learning Questions</span>
          </>
        )}
      </button>

      {!conversationSummary || !conversationSummary.trim() ? (
        <p className="mt-2 text-xs text-gray-500 text-center">
          Generate a summary first to create learning questions
        </p>
      ) : null}

      {isExploring && (
        <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-md text-blue-700 text-sm text-center">
          Exploring topic: {exploringTopic}...
        </div>
      )}
    </div>
  );
};

export default LearningMode;


